/**
 * Scale utility functions for consistent scale value handling
 * Prevents extreme scale values and ensures proper conversion between slider and actual scale
 */

// Scale constants
export const SCALE_CONSTANTS = {
  MIN: 10,
  MAX: 50,
  DEFAULT: 30,
  SLIDER_MIN: 10,
  SLIDER_MAX: 50,
  SLIDER_STEP: 0.1,
};

/**
 * Validates and sanitizes a scale value
 * @param {number} scaleValue - The scale value to validate
 * @param {number} defaultValue - Default value if invalid
 * @returns {number} Sanitized scale value
 */
export const sanitizeScale = (scaleValue, defaultValue = SCALE_CONSTANTS.DEFAULT) => {
  try {
    const numValue = typeof scaleValue === 'string' ? parseFloat(scaleValue) : scaleValue;
    if (isNaN(numValue) || !isFinite(numValue)) {
      console.warn(`Invalid scale value: ${scaleValue}, using default: ${defaultValue}`);
      return defaultValue;
    }
    const clampedValue = Math.max(SCALE_CONSTANTS.MIN, Math.min(SCALE_CONSTANTS.MAX, numValue));
    return clampedValue;
  } catch (error) {
    console.error('Error sanitizing scale value:', error);
    return defaultValue;
  }
};

/**
 * Converts actual scale value (1-50) to slider value (2-100)
 * @param {number} actualScale - Actual scale value (1-50)
 * @returns {number} Slider value (2-100)
 */
export const scaleToSlider = (actualScale) => {
  try {
    const sanitizedScale = sanitizeScale(actualScale);
    const sliderValue = Math.round(sanitizedScale);
    return Math.max(SCALE_CONSTANTS.SLIDER_MIN, Math.min(SCALE_CONSTANTS.SLIDER_MAX, sliderValue));
  } catch (error) {
    console.error('Error converting scale to slider value:', error);
    return SCALE_CONSTANTS.DEFAULT; // Default to middle slider position
  }
};

/**
 * Converts slider value (2-100) to actual scale value (1-50)
 * @param {number} sliderValue - Slider value (2-100)
 * @returns {number} Actual scale value (1-50)
 */
export const sliderToScale = (sliderValue) => {
  try {
    const numValue = typeof sliderValue === 'string' ? parseFloat(sliderValue) : sliderValue;
    if (isNaN(numValue) || !isFinite(numValue)) {
      return SCALE_CONSTANTS.DEFAULT;
    }

    const actualScale = Math.round(numValue);
    return sanitizeScale(actualScale);
  } catch (error) {
    console.error('Error converting slider to scale value:', error);
    return SCALE_CONSTANTS.DEFAULT;
  }
};

/**
 * Safely sets logo scale with validation
 * @param {Object} logo - Logo object
 * @param {number} scaleValue - Scale value to set
 * @param {boolean} isSliderValue - Whether the value is a slider value (true) or actual scale (false)
 * @returns {number} The actual scale value that was set
 */
export const setLogoScale = (logo, scaleValue, isSliderValue = false) => {
  try {
    if (!logo) {
      return SCALE_CONSTANTS.DEFAULT;
    }

    let actualScale;
    if (isSliderValue) {
      actualScale = sliderToScale(scaleValue);
    } else {
      actualScale = sanitizeScale(scaleValue);
    }

    // Set the scale on the logo object
    logo.Scale = actualScale;
    console.log(logo);

    return actualScale;
  } catch (error) {
    console.error('Error setting logo scale:', error);
    const defaultScale = SCALE_CONSTANTS.DEFAULT;
    if (logo) {
      logo.Scale = defaultScale;
    }
    return defaultScale;
  }
};

/**
 * Gets the current scale from a logo object with validation
 * @param {Object} logo - Logo object
 * @returns {number} Validated scale value
 */
export const getLogoScale = (logo) => {
  try {
    if (!logo) {
      return SCALE_CONSTANTS.DEFAULT;
    }

    const currentScale = logo.Scale;
    return sanitizeScale(currentScale);
  } catch (error) {
    console.error('Error getting logo scale:', error);
    return SCALE_CONSTANTS.DEFAULT;
  }
};

/**
 * Formats scale value for display
 * @param {number} scaleValue - Scale value to format
 * @param {boolean} isSliderValue - Whether the value is a slider value
 * @returns {string} Formatted scale value
 */
export const formatScaleValue = (scaleValue, isSliderValue = false) => {
  try {
    let actualScale;
    if (isSliderValue) {
      actualScale = sliderToScale(scaleValue);
    } else {
      actualScale = sanitizeScale(scaleValue);
    }

    return actualScale.toString();
  } catch (error) {
    console.error('Error formatting scale value:', error);
    return SCALE_CONSTANTS.DEFAULT.toString();
  }
};

/**
 * Checks if a scale value is within valid range
 * @param {number} scaleValue - Scale value to check
 * @returns {boolean} Whether the value is valid
 */
export const isValidScale = (scaleValue) => {
  try {
    const numValue = typeof scaleValue === 'string' ? parseFloat(scaleValue) : scaleValue;
    return (
      !isNaN(numValue) &&
      isFinite(numValue) &&
      numValue >= SCALE_CONSTANTS.MIN &&
      numValue <= SCALE_CONSTANTS.MAX
    );
  } catch (error) {
    return false;
  }
};

/**
 * Resets logo scale to default value
 * @param {Object} logo - Logo object
 * @returns {number} The default scale value
 */
export const resetLogoScale = (logo) => {
  try {
    if (!logo) {
      return SCALE_CONSTANTS.DEFAULT;
    }

    logo.Scale = SCALE_CONSTANTS.DEFAULT;
    console.log(`Logo scale reset to default: ${SCALE_CONSTANTS.DEFAULT}`);

    return SCALE_CONSTANTS.DEFAULT;
  } catch (error) {
    console.error('Error resetting logo scale:', error);
    return SCALE_CONSTANTS.DEFAULT;
  }
};
